import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Cable } from 'lucide-react';
import { useStore } from '../../store/StoreContext';
import { fetchMapping } from '../../api/dataApi';
import { ScreenHeader } from '../../components/ui/ScreenHeader';
import { AppCard } from '../../components/ui/AppCard';
import { Button } from '../../components/ui/Button';
import { EmptyState } from '../../components/ui/EmptyState';
import type { Mapping } from '../../types';

export function MappingDetailScreen() {
  const { mappingId } = useParams();
  const { mappings, isMappingOwner } = useStore();

  // `mappings` (GET /mappings) is owner-scoped, so a public mapping owned
  // by someone else won't be there - fall back to a direct fetch by
  // nickname. `mappingId` here is the local id, which equals the nickname
  // for a fetched doc; for a local one it may differ, so prefer the match.
  const local = mappings?.find((m) => m.id === mappingId || m.nickname === mappingId);
  const [fetched, setFetched] = useState<Mapping | null>(null);
  const [loading, setLoading] = useState(!local);

  useEffect(() => {
    if (local || !mappingId) return;
    let cancelled = false;
    fetchMapping(mappingId)
      .then((m) => {
        if (!cancelled) setFetched(m);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mappingId, local]);

  const mapping = local ?? fetched;

  return (
    <div className="flex flex-col gap-4">
      <ScreenHeader
        title="Mapping"
        backTo="/mappings"
        action={
          mapping && isMappingOwner(mapping) ? (
            <Link to={`/mappings/${mapping.id}/edit`}>
              <Button variant="bordered">Edit</Button>
            </Link>
          ) : undefined
        }
      />

      {!mapping ? (
        loading ? (
          <div className="py-10 text-center text-sm text-black/40 dark:text-white/40">Loading…</div>
        ) : (
          <EmptyState icon={Cable} title="Mapping not found" subtitle="It may be private or no longer exist." />
        )
      ) : (
        <>
          <AppCard className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold">{mapping.title || mapping.nickname}</span>
              {mapping.isPublic && (
                <span className="rounded-full bg-blue-600/15 text-blue-600 dark:text-blue-400 text-[11px] font-medium px-2 py-0.5">
                  Public
                </span>
              )}
            </div>
            <span className="font-mono text-black/50 dark:text-white/50">{mapping.nickname}</span>
            {mapping.sourceType && (
              <span className="text-sm">
                Source: <span className="font-mono">{mapping.sourceType}</span>
              </span>
            )}
            {mapping.description && (
              <p className="text-sm text-black/60 dark:text-white/60 m-0">{mapping.description}</p>
            )}
          </AppCard>

          <div className="flex flex-col gap-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-black/40 dark:text-white/40 px-1">Spec</span>
            <pre className="rounded-2xl border border-black/[0.06] dark:border-white/[0.08] bg-white/70 dark:bg-white/[0.06] p-4 overflow-x-auto text-[13px] font-mono leading-relaxed m-0">
              {JSON.stringify(mapping.spec ?? {}, null, 2)}
            </pre>
          </div>
        </>
      )}
    </div>
  );
}
